/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import { Label, Text, TextInput, Textarea, UnstyledButton } from '@/shared/ui';
import { cn, tailwindUtils } from '@/shared/ui/utils/tailwind';
import { drawerSubmitButtonClass } from '@/shared/ui/utils/drawer-submit-button';
import {
  drawerLabelStyle as labelStyle,
  drawerErrorStyle as errorStyle,
} from '@/shared/styles/drawer-field-styles';

/** A mask helper from `shared/utils/input-masks`. */
export type MaskFn = (value: string) => { masked: string; unmasked: string };

export type ProfileFieldProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  placeholder?: string;
  type?: 'text' | 'email';
  /** Masks the displayed value while the form keeps the raw one. */
  mask?: MaskFn;
  maxLength?: number;
};

/**
 * One labelled field of the profile drawers, following the shared drawer
 * pattern: quiet label, unstyled `TextInput`, validation message below.
 *
 * Masked fields display the formatted value but store the raw one, so the
 * API keeps receiving digits and re-opening the drawer shows the mask.
 *
 * @param props - Field wiring, label, and optional mask/textarea options.
 * @returns The rendered field.
 */
export function ProfileField<T extends FieldValues>({
  control,
  name,
  label,
  required = false,
  placeholder,
  type = 'text',
  mask,
  maxLength,
}: ProfileFieldProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div className="flex flex-col">
          <Label style={labelStyle}>
            {label}
            {required && ' *'}
          </Label>
          <TextInput
            {...field}
            value={mask ? mask(String(field.value ?? '')).masked : String(field.value ?? '')}
            onChange={(e) => field.onChange(mask ? mask(e.target.value).unmasked : e.target.value)}
            aria-label={label}
            placeholder={placeholder}
            type={type}
            maxLength={maxLength}
          />
          {fieldState.error?.message && (
            <Text component="span" role="alert" style={errorStyle}>
              {fieldState.error.message}
            </Text>
          )}
        </div>
      )}
    />
  );
}

export type ProfileTextAreaFieldProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label: string;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
};

/**
 * The multi-line sibling of {@link ProfileField}, for free text such as the
 * agency description.
 *
 * @param props - Field wiring, label, and textarea sizing.
 * @returns The rendered field.
 */
export function ProfileTextAreaField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  rows = 6,
  maxLength,
}: ProfileTextAreaFieldProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div className="flex flex-col">
          <Label style={labelStyle}>{label}</Label>
          <Textarea
            {...field}
            value={String(field.value ?? '')}
            aria-label={label}
            placeholder={placeholder}
            rows={rows}
            maxLength={maxLength}
          />
          {fieldState.error?.message && (
            <Text component="span" role="alert" style={errorStyle}>
              {fieldState.error.message}
            </Text>
          )}
        </div>
      )}
    />
  );
}

export type ProfileDrawerSubmitButtonProps = {
  /** The form id this button submits. */
  form: string;
  label: string;
  savingLabel: string;
  disabled: boolean;
};

/**
 * Footer submit button for the profile drawers (auto width, not full-bleed),
 * matching the client and deal drawers.
 *
 * @param props - The target form id, labels, and disabled flag.
 * @returns The rendered submit button.
 */
export function ProfileDrawerSubmitButton({
  form,
  label,
  savingLabel,
  disabled,
}: ProfileDrawerSubmitButtonProps) {
  return (
    <UnstyledButton
      type="submit"
      form={form}
      disabled={disabled}
      aria-label={label}
      data-testid={`${form}-submit`}
      className={cn(
        tailwindUtils.buttonBase,
        drawerSubmitButtonClass,
      )}
    >
      {disabled ? savingLabel : label}
    </UnstyledButton>
  );
}
